import React, { useState, useEffect } from 'react';
import {
  clearStoredAdminToken,
  getAdminMe,
  getStoredAdminToken,
} from '../services/adminAuthApi';

const Navbar = ({ quoteCartCount = 0 }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeLink, setActiveLink] = useState('inicio');
  const [adminInitials, setAdminInitials] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };

    const handleHashChange = () => {
      const hash = (window.location.hash.slice(1) || 'inicio').split('?')[0];
      setActiveLink(hash);
    };

    handleHashChange();
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('hashchange', handleHashChange);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    const loadAdminSession = async () => {
      const token = getStoredAdminToken();
      if (!token) {
        if (mounted) setAdminInitials('');
        return;
      }

      try {
        const admin = await getAdminMe(token);
        if (!mounted) return;
        const email = admin?.email || '';
        const localPart = email.split('@')[0] || '';
        const parts = localPart
          .replace(/[^a-zA-Z0-9]+/g, ' ')
          .trim()
          .split(' ')
          .filter(Boolean);

        let initials = '';
        if (parts.length >= 2) {
          initials = `${parts[0][0]}${parts[1][0]}`;
        } else if (parts.length === 1) {
          initials = parts[0].slice(0, 2);
        }

        setAdminInitials((initials || 'AD').toUpperCase());
      } catch (error) {
        clearStoredAdminToken();
        if (mounted) setAdminInitials('');
      }
    };

    loadAdminSession();

    const handleAuthChanged = () => {
      loadAdminSession();
    };

    window.addEventListener('verissa-admin-auth-changed', handleAuthChanged);

    return () => {
      mounted = false;
      window.removeEventListener('verissa-admin-auth-changed', handleAuthChanged);
    };
  }, []);

  return (
    <nav className={`navbar ${isScrolled ? 'scrolled' : ''}`}>
      <div className="nav-container">
        <a
          href="#inicio"
          className="logo-container"
          aria-label="Ir al inicio"
          onClick={(e) => {
            e.preventDefault();
            window.location.hash = 'inicio';
            setActiveLink('inicio');
            setIsMobileMenuOpen(false);
          }}
        >
          <img src="/assets/Logos/logo.png" alt="VERISSA Diseño Interior" className="logo" />
        </a>
        <ul className={`nav-menu ${isMobileMenuOpen ? 'active' : ''}`}>
          <li>
            <a 
              href="#inicio" 
              className={`nav-link ${activeLink === 'inicio' ? 'active' : ''}`} 
              onClick={(e) => {
                e.preventDefault();
                window.location.hash = 'inicio';
                setActiveLink('inicio');
              }}
            >
              Inicio
            </a>
          </li>
          <li>
            <a 
              href="#catalogos" 
              className={`nav-link ${activeLink === 'catalogos' ? 'active' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                window.location.hash = 'catalogos';
                setActiveLink('catalogos');
              }}
            >
              Catálogos
            </a>
          </li>
          <li>
            <a 
              href="#nosotros" 
              className={`nav-link ${activeLink === 'nosotros' ? 'active' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                window.location.hash = 'nosotros';
                setActiveLink('nosotros');
              }}
            >
              Nosotros
            </a>
          </li>
          <li>
            <a 
              href="#productos" 
              className={`nav-link ${activeLink === 'productos' ? 'active' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                window.location.hash = 'productos';
                setActiveLink('productos');
              }}
            >
              Productos
            </a>
          </li>
          <li>
            <a
              href="#carrito"
              className={`nav-link ${activeLink === 'carrito' ? 'active' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                window.location.hash = 'carrito';
                setActiveLink('carrito');
                setIsMobileMenuOpen(false);
              }}
            >
              Carrito de Cotización
              {quoteCartCount > 0 && (
                <span
                  className="nav-cart-count"
                  aria-label={`${quoteCartCount} ${
                    quoteCartCount === 1 ? 'producto' : 'productos'
                  } en el carrito`}
                >
                  {quoteCartCount}
                </span>
              )}
            </a>
          </li>
          <li>
            <a
              href="#contacto"
              className={`nav-link ${activeLink === 'contacto' ? 'active' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                window.location.hash = 'contacto';
                setActiveLink('contacto');
              }}
            >
              Contacto
            </a>
          </li>
        </ul>
        <div className="nav-actions">
          <button className="icon-btn" aria-label="Buscar">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
          </button>
          <button
            className={`icon-btn ${adminInitials ? 'icon-btn-admin-active' : ''}`}
            aria-label="Usuario"
            title={adminInitials ? 'Sesión admin activa' : 'Acceso administrador'}
            onClick={() => {
              window.location.hash = adminInitials ? 'admin' : 'admin-login';
              setActiveLink(adminInitials ? 'admin' : 'admin-login');
              setIsMobileMenuOpen(false);
            }}
          >
            {adminInitials ? (
              <span className="admin-initials-badge">{adminInitials}</span>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
            )}
          </button>
          <button className="btn-primary">Contáctanos</button>
        </div>
        <button 
          className="hamburger" 
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>
    </nav>
  );
};

export default Navbar;

